import { Link } from 'react-router';
import { ArrowUpRightIcon } from '@heroicons/react/16/solid';
import { Container } from '@/components/layout-primitives';
import { BOOKING_URL, CONTACT_URL } from '@/lib/bw';
import { SERVICES_ENABLED, services } from '@/lib/services';

/**
 * Site footer. Rendered by the layout, so every page carries it.
 *
 * Laid out on a 12-column grid rather than as equal columns. The groups are
 * wildly uneven -- five services against two legal links -- so equal columns
 * left the right-hand half of the footer empty while the services list ran on
 * alone. Services takes a double-width slot and splits into two sub-columns,
 * which brings every group to roughly the same height.
 *
 * The services group is the only place those pages are linked from in the site
 * chrome -- they are deliberately out of the main nav -- so it is carrying the
 * whole of their discoverability and their internal linking. Do not thin it
 * out without giving them another entry point first.
 *
 * That whole group disappears when SERVICES_ENABLED is false, and the grid
 * re-splits so the remaining three still fill the row rather than leaving the
 * right-hand half empty.
 */

const TERMS_URL =
  'https://butterwebsites.notion.site/Butter-Websites-Terms-Conditions-26cc507ef3544ea8981e69e5aeb9e2b1?pvs=4';
const PRIVACY_URL =
  'https://butterwebsites.notion.site/Butter-Websites-Privacy-Policy-77b21aa74f2643a785bafa32f8e7f388?pvs=4';

interface FooterLink {
  label: string;
  href: string;
  external?: boolean;
}

const serviceLinks: FooterLink[] = services.map((service) => ({
  label: service.name,
  href: `/services/${service.slug}`,
}));

/* Mirrors the header, so someone who has scrolled to the bottom does not have
   to scroll back up to find the same destinations -- including the two ways to
   get in touch, which otherwise only exist in the bar at the top and in the
   CTA band. Both of those are off-site, so they carry `external`.

   "All services" only appears when there is a /services index to point at. */
const companyLinks: FooterLink[] = [
  ...(SERVICES_ENABLED ? [{ label: 'All services', href: '/services' }] : []),
  { label: 'Our work', href: '/#work' },
  { label: 'Pricing', href: '/#pricing' },
  { label: 'FAQ', href: '/#faq' },
  { label: 'Book a call', href: BOOKING_URL, external: true },
  { label: 'Contact us', href: CONTACT_URL, external: true },
];

/* 3 + 5 + 2 + 2 with the services column, 6 + 3 + 3 without it. Either way the
   twelve columns are used up, so the row has no dead half. */
const brandSpan = SERVICES_ENABLED ? 'lg:col-span-3' : 'lg:col-span-6';
const groupSpan = SERVICES_ENABLED ? 'lg:col-span-2' : 'lg:col-span-3';

const legalLinks: FooterLink[] = [
  { label: 'Terms & conditions', href: TERMS_URL, external: true },
  { label: 'Privacy policy', href: PRIVACY_URL, external: true },
];

/* No underline at rest -- four stacked groups of dotted-underlined links read
   as noise rather than as navigation. The underline arrives on hover, where it
   confirms the target instead of decorating twelve of them at once.

   Padding grows each target to 44px; the matching negative margin keeps the
   column rhythm exactly as designed. ink-300 rather than ink-500: ink-500 on
   ink is about 3.3:1, under the 4.5:1 minimum for text this size. */
const footerLink =
  '-my-2 inline-flex w-fit rounded-[4px] py-2 text-[15px] leading-[1.35] text-ink-300 transition-colors hover:text-bw-yellow hover:underline hover:decoration-dotted hover:underline-offset-4 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-bw-yellow focus-visible:ring-offset-2 focus-visible:ring-offset-bw-ink';

function FooterNav({
  heading,
  links,
  className = '',
  listClassName = '',
}: {
  heading: string;
  links: FooterLink[];
  className?: string;
  listClassName?: string;
}) {
  return (
    <nav aria-label={heading} className={`flex flex-col gap-4 ${className}`}>
      <h2 className="bw-eyebrow text-white">{heading}</h2>
      <ul
        className={`flex flex-col items-center gap-3 md:items-start ${listClassName}`}
      >
        {links.map((link) => (
          <li key={link.href} className="flex">
            {/* Off-site destinations are plain anchors -- a router Link would
                try to resolve cal.com or a Notion page as an in-app route. */}
            {link.external ? (
              <a
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className={footerLink}
              >
                {link.label}
              </a>
            ) : (
              <Link to={link.href} className={footerLink}>
                {link.label}
              </Link>
            )}
          </li>
        ))}
      </ul>
    </nav>
  );
}

export function SiteFooter() {
  return (
    <footer className="border-t-2 border-bw-ink bg-bw-ink py-16 text-white">
      <Container>
        <div className="grid gap-12 text-center md:grid-cols-2 md:text-left lg:grid-cols-12 lg:gap-8">
          {/* ------------------------------------------------------- Brand */}
          <div
            className={`flex flex-col items-center gap-4 md:items-start ${brandSpan}`}
          >
            {/* The reversed badge (BW_Logo_Badge_REV_Full) is cream-and-butter
                artwork drawn for dark surfaces, so it sits straight on the ink
                -- no yellow tile behind it, which is what the black badge
                needed to survive here at all.

                Sized by height with width auto: the source is 6001x6001 with
                64.6% transparent padding, so it is trimmed to its ink bounds
                (3306x3860) and downscaled. Forcing a square would squash it,
                since the art's bounds are taller than they are wide. */}
            <img
              src="/badge-rev-full.png"
              alt="Butter Websites"
              height={1051}
              width={900}
              loading="lazy"
              decoding="async"
              className="h-[88px] w-auto"
            />

            <p className="max-w-[340px] text-[15px] leading-[1.6] text-ink-300">
              We build, launch, and manage Shopify stores for growing
              businesses.
            </p>

            <div className="flex flex-col items-center gap-1 md:items-start">
              <a
                href="mailto:taylor@butterwebsites.com"
                className="-my-2 rounded-[4px] py-2 font-display text-[17px] font-bold text-bw-yellow underline decoration-dotted underline-offset-4 hover:text-white focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-bw-yellow focus-visible:ring-offset-2 focus-visible:ring-offset-bw-ink"
              >
                taylor@butterwebsites.com
              </a>
              <p className="text-[15px] text-ink-300">Made with 💛 in Ohio</p>
            </div>

            {/* Moved in from a column of its own, where a single lockup under a
                heading left three quarters of that column empty. The inverted
                artwork is light-on-dark, so it reads on the ink directly. */}
            <img
              src="/ShopifyPartners_Primary_Inverted.png"
              alt="Shopify Partners"
              height={110}
              width={150}
              loading="lazy"
              decoding="async"
              className="h-auto w-[120px]"
            />
          </div>

          {/* ------------------------------------------------------- Links */}
          {SERVICES_ENABLED ? (
            <FooterNav
              heading="Services"
              links={serviceLinks}
              className="lg:col-span-5"
              /* Two sub-columns so the services do not run twice as long as
                 every other group. */
              listClassName="sm:grid sm:grid-cols-2 sm:gap-x-6"
            />
          ) : null}
          <FooterNav
            heading="Company"
            links={companyLinks}
            className={groupSpan}
          />
          <FooterNav heading="Legal" links={legalLinks} className={groupSpan} />
        </div>

        {/* ---------------------------------------------------- Bottom bar */}
        {/* Ruled off and laid out along one line. Two items rather than three
            since the pageview odometer was retired, so this is a simple
            space-between row instead of a three-column grid with a hole in the
            middle. */}
        <div className="mt-12 flex flex-col items-center gap-6 border-t border-ink-700 pt-8 md:flex-row md:justify-between md:gap-4">
          <p className="order-2 text-center text-[13px] text-ink-300 md:order-1 md:text-left">
            &copy; Butter Websites LLC 2026. All rights reserved.
          </p>

          {/* The game has its own document shell -- different <html>/<body>
              classes and a locked viewport -- so this stays a plain anchor and
              a hard navigation, which is what we want: it leaves the site
              behind. */}
          <a
            href="/play/stack"
            className="group order-1 inline-flex items-center gap-1.5 rounded-full border border-ink-700 px-4 py-2 text-[13px] text-ink-300 transition-colors hover:border-bw-yellow hover:text-bw-yellow focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-bw-yellow focus-visible:ring-offset-2 focus-visible:ring-offset-bw-ink md:order-2"
          >
            Bored? Stack some butter
            <ArrowUpRightIcon
              aria-hidden="true"
              className="h-3.5 w-3.5 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
            />
          </a>
        </div>
      </Container>
    </footer>
  );
}
